import { TriangleAlertIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';

type ErrorStateProps = {
  /** Defaults to "Something went wrong". */
  title?: ReactNode;
  /** What happened and what to do next. Defaults to a generic line. */
  description?: ReactNode;
  /** Shows a "Try again" button when given. */
  onRetry?: () => void;
  size?: 'default' | 'compact';
  className?: string;
};

/** What a region shows when loading it failed: an alert with a retry. */
function ErrorState({ title, description, onRetry, size = 'default', className }: ErrorStateProps) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      data-slot="error-state"
      data-size={size}
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border border-status-danger/30 bg-status-danger-bg text-center',
        size === 'compact' ? 'px-4 py-6' : 'px-6 py-10',
        className,
      )}
    >
      <TriangleAlertIcon aria-hidden="true" className="size-6 text-status-danger" />
      <p className="font-bold text-heading">{title ?? t('errorState.title')}</p>
      <p className="max-w-prose text-sm text-foreground">
        {description ?? t('errorState.description')}
      </p>
      {onRetry ? (
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          {t('retry')}
        </Button>
      ) : null}
    </div>
  );
}

export { ErrorState, type ErrorStateProps };
